import React, { useEffect, useState } from 'react';
import { api } from '../../App';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const LiveMarketAdminCard = () => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);
  const load = () => api.get('/sb-live/admin/overview').then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const runPayouts = async () => {
    setRunning(true);
    try {
      const r = await api.post('/sb-live/admin/run-payouts');
      toast.success(`Payout run finished: ${r.data.results.length} business(es) checked`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payout run failed');
    } finally {
      setRunning(false);
    }
  };

  const sendSummary = async () => {
    setSending(true);
    try {
      const r = await api.post('/sb-live/admin/send-weekly-emails');
      const owners = r.data.owners?.filter((o) => o.sent).length || 0;
      if (r.data.admin_sent) toast.success(`Summary sent to you${owners ? ` and ${owners} business owner(s)` : ''}. ${r.data.payouts} payout(s) included.`);
      else toast.error('The summary email could not be sent. Check the email service.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the summary');
    } finally {
      setSending(false);
    }
  };

  if (!data) return null;
  const s = data.stripe || {};
  return (
    <Card className="bg-[#111] border-[#00E676]/30" data-testid="admin-live-market-card" data-sb-live>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white">Small Business Market: real money</CardTitle>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={sendSummary} disabled={sending} className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 disabled:opacity-40" data-testid="admin-send-summary-btn">
            {sending ? 'Sending…' : 'Send payout summary now'}
          </Button>
          <Button size="sm" onClick={runPayouts} disabled={running} className="btn-primary" data-testid="admin-run-payouts-btn">Send business payouts now</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {s.charges_enabled === false && (
          <p className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300" data-testid="admin-stripe-charges-warning">
            Your Stripe account cannot take card payments yet. Finish activation in your Stripe dashboard.
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Gross card sales" value={money(data.gross_sales)} id="admin-live-gross" />
          <Stat label="Sraivest revenue (75%)" value={money(data.sraivest_revenue)} id="admin-live-revenue" />
          <Stat label="Owed/paid to businesses" value={money(data.business_earnings_total)} id="admin-live-business" />
          <Stat label="Stripe available" value={s.available_usd != null ? money(s.available_usd) : '—'} id="admin-live-stripe-balance" />
        </div>
        {data.businesses?.length > 0 && (
          <div className="space-y-1">
            {data.businesses.map((b) => (
              <p key={b.business_id} className="text-gray-300" data-testid={`admin-live-biz-${b.business_id}`}>
                {b.name}: {money(b.available)} waiting · {money(b.paid_out)} paid {!b.owner_email && <span className="text-[#F7931A]">(no owner email on file)</span>}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, id }) => (
  <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-gray-500">{label}</p><p className="text-white font-bold" data-testid={id}>{value}</p></div>
);
